import React, { useState, useEffect } from 'react';
import API from '../../services/api.ts';
import { toast } from 'react-toastify';
import './Patients.css';

const PatientDatasets = ({ patient, onDatasetsUpdated }) => {
  const [datasets, setDatasets] = useState([]);
  const [patientDatasets, setPatientDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [processing, setProcessing] = useState(false);

  // Create new dataset state
  const [showCreateDatasetModal, setShowCreateDatasetModal] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState('');
  const [newDatasetDescription, setNewDatasetDescription] = useState('');
  const [creatingDataset, setCreatingDataset] = useState(false);

  // Fetch all datasets and patient's datasets
  useEffect(() => {
    if (patient) {
      fetchDatasets();
      fetchPatientDatasets();
    }
  }, [patient]);

  const fetchDatasets = async () => {
    setLoadingDatasets(true);
    try {
      const response = await API.get('/datasets/');
      setDatasets(response.data);
    } catch (err) {
      console.error('Error fetching datasets:', err);
      toast.error('Failed to fetch datasets.');
    } finally {
      setLoadingDatasets(false);
    }
  };

  const fetchPatientDatasets = async () => {
    try {
      // Get all datasets and filter for ones containing this patient
      const response = await API.get('/datasets/');
      const patientInDatasets = response.data.filter(dataset => {
        // Check if dataset has this patient (we'll need to fetch full dataset details)
        return false; // Placeholder - will implement properly
      });

      // Alternative: Fetch patient details which might include dataset info
      // For now, we'll fetch each dataset's patients to check
      const datasetsWithPatient = [];
      for (const dataset of response.data) {
        try {
          const patientsResponse = await API.get(`/datasets/${dataset.id}/patients/`);
          const hasPatient = patientsResponse.data.some(p => p.id === patient.id);
          if (hasPatient) {
            datasetsWithPatient.push(dataset);
          }
        } catch (err) {
          // Silently continue if we can't fetch patients for this dataset
          continue;
        }
      }
      setPatientDatasets(datasetsWithPatient);
    } catch (err) {
      console.error('Error fetching patient datasets:', err);
    }
  };

  const handleAddToDataset = async () => {
    if (!selectedDatasetId) {
      toast.warning('Please select a dataset.');
      return;
    }

    setProcessing(true);
    try {
      await API.post(`/datasets/${selectedDatasetId}/add-patients/`, {
        patient_ids: [patient.id]
      });
      toast.success('Patient added to dataset successfully!');
      setShowAddModal(false);
      setSelectedDatasetId('');
      await fetchPatientDatasets();
      if (onDatasetsUpdated) {
        onDatasetsUpdated();
      }
    } catch (err) {
      console.error('Error adding patient to dataset:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to add patient to dataset.';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveFromDataset = async (datasetId) => {
    if (!window.confirm('Are you sure you want to remove this patient from the dataset?')) {
      return;
    }

    setProcessing(true);
    try {
      await API.post(`/datasets/${datasetId}/remove-patients/`, {
        patient_ids: [patient.id]
      });
      toast.success('Patient removed from dataset successfully!');
      await fetchPatientDatasets();
      if (onDatasetsUpdated) {
        onDatasetsUpdated();
      }
    } catch (err) {
      console.error('Error removing patient from dataset:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to remove patient from dataset.';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  // Get datasets that the patient is NOT in
  const availableDatasets = datasets.filter(
    dataset => !patientDatasets.some(pd => pd.id === dataset.id)
  );

  // Create new dataset with this patient
  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) {
      toast.warning('Please enter a dataset name.');
      return;
    }

    setCreatingDataset(true);
    try {
      // Create the dataset
      const createResponse = await API.post('/datasets/', {
        name: newDatasetName.trim(),
        description: newDatasetDescription.trim() || null
      });

      const newDataset = createResponse.data;

      // Add the current patient to the new dataset
      await API.post(`/datasets/${newDataset.id}/add-patients/`, {
        patient_ids: [patient.id]
      });

      toast.success(`Dataset "${newDatasetName}" created and patient added!`);
      setShowCreateDatasetModal(false);
      setNewDatasetName('');
      setNewDatasetDescription('');

      // Refresh datasets
      await fetchDatasets();
      await fetchPatientDatasets();

      if (onDatasetsUpdated) {
        onDatasetsUpdated();
      }
    } catch (err) {
      console.error('Error creating dataset:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to create dataset.';
      toast.error(errorMsg);
    } finally {
      setCreatingDataset(false);
    }
  };

  return (
    <div className="patient-datasets-container">
      <div className="datasets-header">
        <div>
          <h6 className="mb-1">
            <i className="fa fa-database me-2"></i>
            Datasets
          </h6>
          <p className="text-muted small mb-0">
            Datasets this patient belongs to
          </p>
        </div>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setShowAddModal(true)}
          disabled={processing || loadingDatasets}
        >
          <i className="fa fa-plus me-2"></i>
          Add to Dataset
        </button>
      </div>

      {/* Patient Datasets List */}
      <div className="datasets-list mt-3">
        {patientDatasets.length === 0 ? (
          <div className="alert alert-info mb-0">
            <i className="fa fa-info-circle me-2"></i>
            This patient is not part of any dataset yet.
          </div>
        ) : (
          <div className="list-group">
            {patientDatasets.map(dataset => (
              <div key={dataset.id} className="list-group-item dataset-item">
                <div className="dataset-info">
                  <h6 className="mb-1">{dataset.name}</h6>
                  {dataset.description && (
                    <p className="text-muted small mb-0">{dataset.description}</p>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemoveFromDataset(dataset.id)}
                  disabled={processing}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add to Dataset Modal */}
      {showAddModal && (
        <>
          <div className="modal-backdrop show" onClick={() => setShowAddModal(false)}></div>
          <div className="modal show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fa fa-database me-2"></i>
                    Add Patient to Dataset
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <i className="fa fa-info-circle me-2"></i>
                    Select a dataset to add {patient.name || patient.patient_id} to
                  </div>
                  <div className="form-group">
                    <label className="form-label">Select Dataset</label>
                    <select
                      className="form-select"
                      value={selectedDatasetId}
                      onChange={(e) => setSelectedDatasetId(e.target.value)}
                    >
                      <option value="">-- Choose a dataset --</option>
                      {availableDatasets.map(dataset => (
                        <option key={dataset.id} value={dataset.id}>
                          {dataset.name}
                        </option>
                      ))}
                    </select>
                    {availableDatasets.length === 0 && (
                      <p className="text-muted small mt-2">
                        No available datasets. This patient is already in all your datasets.
                      </p>
                    )}
                  </div>
                  <hr className="my-3" />
                  <div className="text-center">
                    <p className="text-muted small mb-2">Or create a new dataset with this patient</p>
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                      onClick={() => {
                        setShowAddModal(false);
                        setShowCreateDatasetModal(true);
                      }}
                    >
                      <i className="fa fa-plus me-2"></i>
                      Create New Dataset
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddToDataset}
                    disabled={!selectedDatasetId || processing}
                  >
                    {processing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Adding...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-plus me-2"></i>
                        Add to Dataset
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Create New Dataset Modal */}
      {showCreateDatasetModal && (
        <>
          <div className="modal-backdrop show" onClick={() => setShowCreateDatasetModal(false)}></div>
          <div className="modal show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fa fa-database me-2"></i>
                    Create New Dataset
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateDatasetModal(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <i className="fa fa-info-circle me-2"></i>
                    Create a new dataset with {patient.name || patient.patient_id} as the first patient
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Dataset Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter dataset name"
                      value={newDatasetName}
                      onChange={(e) => setNewDatasetName(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      placeholder="Enter dataset description (optional)"
                      value={newDatasetDescription}
                      onChange={(e) => setNewDatasetDescription(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateDatasetModal(false)}
                    disabled={creatingDataset}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleCreateDataset}
                    disabled={!newDatasetName.trim() || creatingDataset}
                  >
                    {creatingDataset ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-plus me-2"></i>
                        Create Dataset
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PatientDatasets;
